import React, { useState } from 'react';
import { readUtm } from '../utils/utm.js';

const WEBHOOK_URL = 'https://n8n.aakeeocrm.com/webhook/5fed7f08-2230-4d07-a78b-7db4c26c0246';

const DEFAULTS_BY_TYPE = {
  pdf:         { eyebrow: 'Free · PDF',         cta: 'Send me the PDF' },
  spreadsheet: { eyebrow: 'Free · Spreadsheet', cta: 'Send me the spreadsheet' },
  guide:       { eyebrow: 'Free · Guide',       cta: 'Send me the guide' },
  updates:     { eyebrow: 'Free · Updates',     cta: 'Subscribe' },
};

/**
 * Reusable email capture component.
 *
 * Posts to the shared n8n webhook (https://n8n.aakeeocrm.com/webhook/...) and
 * carries first-touch UTMs from the visitor's session plus page + referrer
 * context. Designed for both inline opt-ins (e.g. /tunnelr) and gated-content
 * use in cornerstone articles.
 *
 * Usage — inline opt-in:
 *   <EmailCapture
 *     source="tunnelr_page"
 *     resourceName="Tunnelr release notes"
 *     resourceType="updates"
 *     title={<>Get notified when new <em>Tunnelr</em> features ship.</>}
 *     description="Occasional email when we ship something worth your attention."
 *     cta="Notify me"
 *   />
 *
 * Usage — content gate (cornerstone articles):
 *   <EmailCapture
 *     source="cornerstone_do_you_need_a_crm"
 *     resourceName="CRM decision worksheet"
 *     resourceType="pdf"
 *     showNameField
 *     title="Get the decision worksheet."
 *     description="The five-question PDF we use with every client scoping call."
 *   />
 *
 * Props:
 *   - source (string, required)  — analytics identifier, e.g. "tunnelr_page"
 *   - resourceName (string)      — human label shown in success UI + logged
 *   - resourceType               — "pdf" | "spreadsheet" | "guide" | "updates"
 *                                  (picks default eyebrow + button copy)
 *   - redirectUrl (string)       — redirect on success instead of success card
 *   - title, description,        — display overrides (title can be ReactNode)
 *     eyebrow, cta
 *   - showNameField (bool)       — render an optional name input
 *   - successTitle, successBody  — copy for the success state
 *
 * Payload sent to n8n (as GET query params, no-cors):
 *   email, name?, source, resource_name, resource_type,
 *   referrer, page, utm_source?, utm_medium?, utm_campaign?,
 *   utm_term?, utm_content?
 *
 * Server-side responsibilities (configured in n8n, not here):
 *   - persist the submission (sheet / DB / file)
 *   - send confirmation email with resource link, branched on resource_type
 *     or source as appropriate
 */
export default function EmailCapture({
  source,
  resourceName = '',
  resourceType = 'updates',
  redirectUrl,
  eyebrow,
  title,
  description,
  cta,
  showNameField = false,
  successTitle = 'Thanks — we got it.',
  successBody,
}) {
  if (!source) {
    // Fail loud in development — we rely on source for analytics and n8n branching.
    // eslint-disable-next-line no-console
    console.warn('<EmailCapture /> rendered without a `source` prop.');
  }
  const defaults = DEFAULTS_BY_TYPE[resourceType] || DEFAULTS_BY_TYPE.updates;

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState(null); // null | 'submitting' | 'success' | 'error'

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || status === 'submitting') return;
    setStatus('submitting');
    try {
      const utm = readUtm();
      const params = new URLSearchParams({
        email: email.trim(),
        ...(showNameField && name.trim() ? { name: name.trim() } : {}),
        source: source || 'unspecified',
        resource_name: resourceName,
        resource_type: resourceType,
        referrer: typeof document !== 'undefined' ? document.referrer || '' : '',
        page: typeof window !== 'undefined' ? window.location.pathname : '',
        ...utm,
      });
      // `no-cors` makes the response opaque (matches the existing contact form
      // + newsletter pattern). We treat a non-throwing fetch as success — real
      // network failures surface as a thrown error and hit the catch below.
      await fetch(`${WEBHOOK_URL}?${params.toString()}`, { method: 'GET', mode: 'no-cors' });
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', 'email_capture_submit', {
          event_category: 'lead_capture',
          event_label: source,
          resource_type: resourceType,
        });
      }
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }
      setStatus('success');
      setEmail('');
      setName('');
    } catch (err) {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <aside className="leadCaptureA leadCaptureA--success">
        <span className="eyebrowA">Sent</span>
        <h3>{successTitle}</h3>
        <p>
          {successBody ||
            "A real person will reach out shortly. No automated drip, no list — just a direct reply when we've got something worth sharing."}
        </p>
      </aside>
    );
  }

  return (
    <aside className="leadCaptureA">
      <div className="leadCaptureA-copy">
        <span className="eyebrowA">{eyebrow || defaults.eyebrow}</span>
        {title && <h3>{title}</h3>}
        {description && <p>{description}</p>}
      </div>
      <form className="leadCaptureA-form" onSubmit={handleSubmit}>
        {showNameField && (
          <input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Name"
          />
        )}
        <input
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          aria-label="Email address"
        />
        <button type="submit" className="btnA-primary" disabled={status === 'submitting'}>
          {status === 'submitting' ? 'Sending…' : (cta || defaults.cta)}
        </button>
      </form>
      {status === 'error' && (
        <p className="leadCaptureA-note leadCaptureA-note--err">
          Something went wrong. Try again, or email us at sales@aakeeo.com.
        </p>
      )}
      <p className="leadCaptureA-fine">No spam. Unsubscribe in one click.</p>
    </aside>
  );
}
